import React, { useState, useEffect, useCallback } from 'react';
import { Award, ChevronLeft, ChevronRight } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { publicAPI } from '../services/api';

// Default slides as fallback - Dark teal theme matching Nanda Diagnostics brand
// Images should be uploaded via admin dashboard - empty by default
const defaultSlides = [
  {
    title: "Your Trusted",
    subtitle: "Diagnostics Center",
    description: "State-of-the-art equipment and highly skilled professionals for accurate diagnostics.",
    bg_gradient: "from-[#006078]/90 to-[#007897]/90",
    image: "",
    image_position: "center 25%"
  },
  {
    title: "Home Collection",
    subtitle: "Services Available",
    description: "Get your samples collected from the comfort of your home by our trained phlebotomists.",
    bg_gradient: "from-[#006078]/90 to-[#007897]/90",
    image: "",
    image_position: "center 30%"
  },
  {
    title: "Complete Health",
    subtitle: "Check-up Packages",
    description: "Comprehensive wellness packages designed for preventive healthcare at affordable prices.",
    bg_gradient: "from-[#006078]/90 to-[#007897]/90",
    image: "",
    image_position: "center 25%"
  }
];

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroSlides, setHeroSlides] = useState(defaultSlides);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [services, setServices] = useState([]);

  // Fetch services from backend
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await publicAPI.getServices();
        if (response.data && response.data.length > 0) {
          setServices(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch services:', error);
      }
    };
    fetchServices();
  }, []);

  // Fetch hero slides from backend
  useEffect(() => {
    const fetchHeroSlides = async () => {
      try {
        const response = await publicAPI.getHeroSlides();
        if (response.data && response.data.length > 0) {
          const activeSlides = response.data
            .filter(slide => slide.is_active !== false)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
          if (activeSlides.length > 0) {
            setHeroSlides(activeSlides);
          }
        }
      } catch (error) {
        console.error('Failed to fetch hero slides, using defaults:', error);
      }
    };
    fetchHeroSlides();
  }, []);

  // Preload all hero images
  useEffect(() => {
    heroSlides.forEach(slide => {
      const img = new Image();
      img.src = slide.image;
    });
  }, [heroSlides]);

  // Change slide with transition lock
  const changeSlide = useCallback((newIndex) => {
    if (isTransitioning || newIndex === currentSlide) return;
    setIsTransitioning(true);
    setCurrentSlide(newIndex);
    setTimeout(() => setIsTransitioning(false), 700);
  }, [isTransitioning, currentSlide]);

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const nextSlide = () => changeSlide((currentSlide + 1) % heroSlides.length);
  const prevSlide = () => changeSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length);

  return (
    <section className="relative min-h-screen pt-16 md:pt-24 overflow-hidden bg-[#006078]">
      {/* All Complete Slides - Each slide contains its own background + content */}
      {heroSlides.map((slide, index) => (
        <div 
          key={`hero-item-${index}`}
          className="absolute inset-0 pt-16 md:pt-24"
          style={{
            opacity: index === currentSlide ? 1 : 0,
            visibility: index === currentSlide ? 'visible' : 'hidden',
            transition: 'opacity 600ms ease-in-out, visibility 600ms ease-in-out',
            zIndex: index === currentSlide ? 2 : 1
          }}
        >
          {/* Background Image for this slide - only show if image exists */}
          <div className="absolute inset-0">
            {slide.image && slide.image.includes('/api/uploads/') && (
              <img 
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
                style={{ objectPosition: slide.image_position || 'center center' }}
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            )}
            {/* Gradient Overlay - always present for consistent branding */}
            <div className={`absolute inset-0 bg-gradient-to-r ${slide.bg_gradient || 'from-[#006078]/90 to-[#007897]/90'}`} />
          </div>

          {/* Slide-specific Text Content - Shifted upwards */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 h-full flex items-center">
            <div className="grid lg:grid-cols-2 gap-8 items-center w-full py-8 lg:py-12">
              <div className="text-white">
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
                  <Award size={18} />
                  <span className="text-sm font-medium">32+ Years of Trust</span>
                </div>
                
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 leading-tight">
                  {slide.title}
                  <span className="block text-blue-200">{slide.subtitle}</span>
                </h1>
                
                <p className="text-base text-blue-100 mb-6 max-w-xl">
                  {slide.description}
                </p>

                {/* Stats - More compact */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="text-center sm:text-left">
                    <div className="text-2xl font-bold">{companyInfo.stats.patients}</div>
                    <div className="text-blue-200 text-xs">Happy Patients</div>
                  </div>
                  <div className="text-center sm:text-left">
                    <div className="text-2xl font-bold">{companyInfo.stats.tests}</div>
                    <div className="text-blue-200 text-xs">Tests Done</div>
                  </div>
                  <div className="text-center sm:text-left">
                    <div className="text-2xl font-bold">{companyInfo.stats.yearsExperience}</div>
                    <div className="text-blue-200 text-xs">Years Experience</div>
                  </div>
                  <div className="text-center sm:text-left">
                    <div className="text-2xl font-bold">{companyInfo.stats.specialists}</div>
                    <div className="text-blue-200 text-xs">Specialists</div>
                  </div>
                </div>
              </div>

              {/* Right Side - Service Cards - Vertically Centered */}
              <div className="hidden lg:flex lg:items-center lg:justify-center">
                <div className="grid grid-cols-2 gap-3 w-full">
                  {services.slice(0, 4).map((service) => (
                    <div
                      key={service.id}
                      className="bg-white/10 backdrop-blur-sm p-4 rounded-2xl cursor-default select-none"
                    >
                      <h3 className="text-white font-semibold mb-1 text-sm">{service.name}</h3>
                      <p className="text-blue-200 text-xs line-clamp-2">{service.shortDescription || service.description?.substring(0, 50) + '...'}</p>
                    </div>
                  ))}
                  {/* Fifth service spans full width */}
                  {services[4] && (
                    <div className="col-span-2 bg-white/10 backdrop-blur-sm p-4 rounded-2xl cursor-default select-none">
                      <h3 className="text-white font-semibold mb-1 text-sm">{services[4].name}</h3>
                      <p className="text-blue-200 text-xs line-clamp-1">{services[4].shortDescription || services[4].description?.substring(0, 80) + '...'}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
      
      {/* Animated Background Pattern - Always visible */}
      <div className="absolute inset-0 opacity-10 z-[3] pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl opacity-10" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl opacity-10" />
      </div>

      {/* Slide Navigation - Always on top */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-4 z-20">
        <button
          onClick={prevSlide}
          disabled={isTransitioning}
          className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors disabled:opacity-50"
          aria-label="Previous slide"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="flex gap-2">
          {heroSlides.map((_, index) => (
            <button
              key={`hero-item-${index}`}
              onClick={() => changeSlide(index)}
              disabled={isTransitioning}
              aria-label={`Go to slide ${index + 1}`}
              className={`h-3 rounded-full transition-all duration-300 ${
                currentSlide === index ? 'bg-white w-8' : 'bg-white/50 w-3 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
        <button
          onClick={nextSlide}
          disabled={isTransitioning}
          className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors disabled:opacity-50"
          aria-label="Next slide"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    </section>
  );
};

export default HeroSection;
